import { format } from 'date-fns'
import { useMemo } from 'react'

import { parseDateOnly } from '@/lib/formatters'
import { type CoverageType, ENABLED_COVERAGES, type EnabledCoverage } from '@/models/insuranceCoverage'
import { updateCoverageNeeds } from '@/models/user'
import { applyServerErrors } from './formServerErrors'
import { type SaveResult, type SyncedForm, useSyncedForm } from './useSyncedForm'

export interface CoverageNeedsSource {
  /** ISO date on file, or null until someone answers. */
  startDate: string | null | undefined
  selected: CoverageType[] | undefined
  /** The coverages this business can choose; trucking's when not given. */
  menu?: ReadonlyArray<EnabledCoverage>
}

export interface CoverageNeedsValues {
  startDate: Date | null
  coverages: CoverageType[]
}

export type CoverageNeedsField = keyof CoverageNeedsValues

export interface CoverageNeedsForm extends SyncedForm<CoverageNeedsValues> {
  save: (orgId: string, options: { onlyChanged: boolean }) => Promise<SaveResult>
}

const ERROR_FIELDS: Record<string, CoverageNeedsField> = {
  desired_effective_date: 'startDate',
  selected_coverages: 'coverages',
}

export function coverageNeedsValues({
  startDate,
  selected,
  menu = ENABLED_COVERAGES,
}: CoverageNeedsSource): CoverageNeedsValues {
  const required = menu.filter((coverage) => coverage.required).map((coverage) => coverage.coverageType)
  return {
    startDate: parseDateOnly(startDate) ?? null,
    // Required coverages render disabled, so an unticked one could never be saved.
    coverages: [...new Set([...required, ...(selected ?? [])])],
  }
}

/** What to PATCH to `coverage-needs/`: both fields, or with `changed` only those. */
export function coverageNeedsPayload(
  values: CoverageNeedsValues,
  changed?: readonly CoverageNeedsField[]
): { desiredEffectiveDate?: string; selectedCoverages?: CoverageType[] } {
  const include = (field: CoverageNeedsField) => !changed || changed.includes(field)
  return {
    ...(include('startDate') && values.startDate
      ? { desiredEffectiveDate: format(values.startDate, 'yyyy-MM-dd') }
      : {}),
    ...(include('coverages') ? { selectedCoverages: values.coverages } : {}),
  }
}

export function useCoverageNeedsForm({ startDate, selected, menu }: CoverageNeedsSource): CoverageNeedsForm {
  const values = useMemo(
    () => coverageNeedsValues({ startDate, selected, menu }),
    [startDate, selected, menu]
  )
  const synced = useSyncedForm(values)

  async function save(orgId: string, { onlyChanged }: { onlyChanged: boolean }): Promise<SaveResult> {
    const { form, changedFields, markSaved } = synced
    form.clearErrors()
    if (onlyChanged && changedFields.length === 0) return { ok: true }
    if (!onlyChanged && !form.getValues('startDate')) return { ok: false }
    try {
      await updateCoverageNeeds(
        orgId,
        coverageNeedsPayload(form.getValues(), onlyChanged ? changedFields : undefined)
      )
      markSaved()
      return { ok: true }
    } catch (error) {
      applyServerErrors(form, error, ERROR_FIELDS)
      return { ok: false, error }
    }
  }

  return { ...synced, save }
}
